import styles from "./styles.scss";
import { useCallback, useEffect, useState, useRef } from "react";
import {
  generatePath,
  Navigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import clsx from "clsx";
import { isApprovalTab } from "./utils";
import { APPROVALS_TAB_PATH, ApprovalsTab } from "@/constants";
import { Header, NoItemsSelected, SideBar } from "./components";
import { InboxDetails } from "@/pages/inbox/components/inbox-details/inbox-details";

import { useSearchSuggests } from "./components/sidebar/hooks/use-search-suggests";
import { useSideBarOperations } from "./components/sidebar/hooks/use-sidebar-operations";
import { InboxKeyParam } from "@/pages/inbox/types";
import { Nullable } from "@/services/api/request/contracts";
import { InboxItem } from "@/services/api/inbox/contracts";
import { selectInboxStore } from "@/services/stores/selectors/inbox";
import { useBoundStore } from "@/services";

export const ApprovalsPage = () => {
  const { setActiveInboxItem } = useBoundStore(selectInboxStore);

  const { tab } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterMode, setIsFilterMode] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const [approvalSuggests, setApprovalSuggests] =
    useState<Nullable<InboxItem[]>>(null);

  const [isInitialLoad, setIsInitialLoad] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  const referenceNumber = searchParams.get(InboxKeyParam.ReferenceNumber);

  const toggleFilterMode = (value: boolean) => {
    setIsFilterMode(value);
  };

  const handleSetApprovalSuggests = (value: Nullable<InboxItem[]>) => {
    setApprovalSuggests(value);
  };

  const {
    dataIsLoading,
    data,
    error,
    isSearchFocus,
    filters,
    appliedFilters,
    hasMoreData,
    isTracking,
    onLoadMoreData,
    onToggleSearchFieldFocus,
    onFocusSearchSuggests,
    onSelectRequest,
    onSearchSubmit,
    onSetFilters,
    onResetFilters,
    onResetData,
    isShowFullSearchList,
    onApplyFilters,
    onUnApplyFilters,
    onGetSelectedRequestData,
    onRemoveRequestFromList,
  } = useSideBarOperations({
    tab: tab || "",
    searchQuery,
    suggests: approvalSuggests,
    searchContainerRef,
    onSetApprovalSuggests: setApprovalSuggests,
    onToggleFilterMode: toggleFilterMode,
    onSetActiveInboxItem: setActiveInboxItem,
  });

  const { onSelectSuggest, onQueryChange, onQueryClear } = useSearchSuggests({
    onResetData,
    onSetApprovalSuggests: handleSetApprovalSuggests,
    onSetSearchQuery: setSearchQuery,
  });

  const [selectedRequest, setSelectedRequest] =
    useState<Nullable<InboxItem>>(null);

  useEffect(() => {
    if (referenceNumber && data) {
      const selectedItem = onGetSelectedRequestData(referenceNumber);

      setActiveInboxItem(selectedItem || null);
    }

    // TODO clarify what use, state or store
    setSelectedRequest(null);

    return () => {
      setActiveInboxItem(null);
    };
  }, [
    data,
    onGetSelectedRequestData,
    referenceNumber,
    setActiveInboxItem,
    tab,
  ]);

  useEffect(() => {
    if (!dataIsLoading && !isInitialLoad) {
      setIsInitialLoad(true);
    }
  }, [dataIsLoading, isInitialLoad]);

  const handleResetSelectedRequest = useCallback(() => {
    // TODO clarify what use, state or store
    setSelectedRequest(null);
    setActiveInboxItem(null);
    setSearchParams();
  }, [setActiveInboxItem, setSearchParams]);

  const sideBarProps = {
    data,
    isLoading: dataIsLoading,
    isError: !!error,
    isFilterMode,
    onToggleFilterMode: toggleFilterMode,
    selectedRequest,
    approvalSuggests,
    onSetApprovalSuggests: setApprovalSuggests,
    isTracking,
    searchContainerRef,
    isSearchFocus,
    searchQuery,
    filters,
    appliedFilters,
    onSetSearchQuery: setSearchQuery,
    onToggleSearchFieldFocus,
    isShowFullSearchList,
    onFocusSearchSuggests,
    onSelectSuggest,
    onSelectRequest,
    onQueryChange,
    onQueryClear,
    onSearchSubmit,
    onSetFilters,
    onResetFilters,
    onApplyFilters,
    onUnApplyFilters,
    onLoadMoreData: onLoadMoreData,
    hasMoreData,
  };

  const detailsProps = {
    selectedRequest,
    onGetSelectedRequestData: onGetSelectedRequestData,
    onResetSelectedRequest: handleResetSelectedRequest,
    onRemoveRequestFromList: onRemoveRequestFromList,
  };

  if (!isApprovalTab(tab)) {
    return (
      <Navigate
        to={generatePath(APPROVALS_TAB_PATH, { tab: ApprovalsTab.Inbox })}
        replace={true}
      />
    );
  }

  return (
    <div className={clsx(styles.approvalsPage)}>
      <Header />

      <div className={styles.layoutContainer}>
        <SideBar {...sideBarProps} />

        {referenceNumber ? (
          <InboxDetails {...detailsProps} />
        ) : (
          (!dataIsLoading || isInitialLoad) && <NoItemsSelected />
        )}
      </div>
    </div>
  );
};
